import { Button } from '@heidi/design-system/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { QueuedMessageListProps } from './QueuedMessageList';
import { queuedMessageListMessages } from './QueuedMessageList.messages';
import { QueuedMessageRow } from './QueuedMessageRow';

/**
 * Pre-ghost queue chrome — bordered card + Clear all. Kept as the kill-switch
 * fallback when `useIsComposerGhostQueueEnabled` is off.
 */
export const QueuedMessageListCard = ({
  items,
  onSendNow,
  onRemove,
  onClear,
  onEdit,
  onMoveToFront,
  onEditingChange,
  autoSend,
  className,
  testId,
}: QueuedMessageListProps): JSX.Element | null => {
  const intl = useIntl();
  const queueCount = items.length;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scrimVisible, setScrimVisible] = useState(false);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const update = () => {
      const overflow = scroller.scrollHeight - scroller.clientHeight > 1;
      const atEnd =
        scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
      setScrimVisible(overflow && !atEnd);
    };
    update();
    scroller.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [queueCount]);

  if (queueCount === 0) return null;

  return (
    <div className={cn('flex w-full flex-col gap-2', className)} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 px-1">
        <TypographyCaption className="min-w-0 truncate text-text-secondary">
          <FormattedMessage
            {...queuedMessageListMessages.queuedCount}
            values={{ count: queueCount }}
          />
        </TypographyCaption>
        <div className="flex shrink-0 items-center gap-2">
          {autoSend ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  aria-pressed={autoSend.enabled}
                  onClick={() => autoSend.onToggle()}
                  className={cn(
                    'h-auto gap-1.5 rounded-md px-2 py-1',
                    autoSend.enabled
                      ? 'bg-accent-forest/15 text-accent-forest'
                      : 'text-text-tertiary hover:bg-surface-hover hover:text-text-secondary'
                  )}
                >
                  <span
                    className={cn(
                      'size-1.5 rounded-full',
                      autoSend.enabled ? 'bg-accent-forest' : 'bg-text-tertiary'
                    )}
                    aria-hidden
                  />
                  <FormattedMessage {...queuedMessageListMessages.autoSend} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[200px]">
                {intl.formatMessage(
                  autoSend.enabled
                    ? queuedMessageListMessages.autoSendOnTooltip
                    : queuedMessageListMessages.autoSendOffTooltip
                )}
              </TooltipContent>
            </Tooltip>
          ) : null}
          {queueCount > 1 ? (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={onClear}
              className={cn(
                'h-auto gap-1 rounded-md px-2 py-1 font-normal',
                'text-text-secondary transition-colors hover:text-validation-destructive'
              )}
              aria-label={intl.formatMessage(queuedMessageListMessages.clearAllAria)}
            >
              <Trash2 size={12} />
              <FormattedMessage {...queuedMessageListMessages.clearAll} />
            </Button>
          ) : null}
        </div>
      </div>
      <div className="relative overflow-hidden rounded-xl border border-border bg-surface-container">
        <div
          ref={scrollerRef}
          data-testid="queued-message-list-scroller"
          className="scrollbar-subtle flex max-h-40 flex-col gap-0.5 overflow-y-auto p-1"
        >
          {items.map((item, index) => (
            <QueuedMessageRow
              key={item.id}
              id={item.id}
              text={item.text}
              secondaryText={item.secondaryText}
              editableText={item.editableText}
              index={index}
              onSendNow={onSendNow}
              onRemove={onRemove}
              onEdit={onEdit}
              onMoveToFront={onMoveToFront}
              onEditingChange={onEditingChange}
            />
          ))}
        </div>
        {scrimVisible ? (
          <div
            data-testid="queued-message-list-scrim"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-surface-container to-surface-container/0"
          />
        ) : null}
      </div>
    </div>
  );
};
